import { useState } from 'preact/hooks';
import { startRegistration } from '@simplewebauthn/browser';
import { api } from '../api';
import { saveSettings, sessionLoading, settings, signOut, user } from '../state/session';
import { setPlaybackRate } from '../state/player';
import { DeleteAccount } from '../components/DeleteAccount';
import { DownloadSettings } from '../components/DownloadSettings';
import { InstallHint } from '../components/InstallHint';
import { NotificationSettings } from '../components/NotificationSettings';
import { OpmlManager } from '../components/OpmlManager';
import { SignInPanel } from '../components/SignInPanel';
import { Spinner } from '../components/Spinner';

export function Settings() {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const me = user.value;

  if (sessionLoading.value) {
    return (
      <div class="view">
        <header class="view__header">
          <h1>Settings</h1>
        </header>
        <Spinner page />
      </div>
    );
  }

  if (!me) {
    return (
      <div class="view">
        <header class="view__header">
          <h1>Settings</h1>
        </header>
        <SignInPanel />
      </div>
    );
  }

  const update = async (patch: Parameters<typeof saveSettings>[0]) => {
    setBusy(true);
    setMessage(null);
    try {
      await saveSettings(patch);
      if (patch.defaultPlaybackRate) setPlaybackRate(patch.defaultPlaybackRate);
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const addPasskey = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const { handle, options } = await api.passkeyRegisterOptions({});
      const response = await startRegistration({ optionsJSON: options as never });
      const { user: updated } = await api.passkeyRegisterVerify({ handle, response });
      user.value = updated;
      setMessage('Passkey added to this device.');
    } catch (err) {
      const name = (err as Error).name;
      setMessage(name === 'NotAllowedError' ? 'Cancelled.' : (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="view">
      <header class="view__header">
        <h1>Settings</h1>
      </header>

      {message && <p class="notice">{message}</p>}

      <section class="section">
        <h2 class="section__title">Account</h2>
        <p class="muted">{me.email ?? 'No email on file'}</p>
        <div class="stack stack--row">
          {!me.hasPasskey && (
            <button type="button" class="button button--ghost" onClick={addPasskey} disabled={busy}>
              Add a passkey
            </button>
          )}
          <button type="button" class="button button--ghost" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </section>

      <section class="section">
        <h2 class="section__title">Playback</h2>

        <label class="field">
          <span>Default speed: {settings.value.defaultPlaybackRate}×</span>
          <input
            type="range"
            min={0.5}
            max={3}
            step={0.1}
            value={settings.value.defaultPlaybackRate}
            disabled={busy}
            onChange={(e) =>
              void update({ defaultPlaybackRate: Number((e.target as HTMLInputElement).value) })
            }
          />
        </label>

        <label class="field">
          <span>Skip forward: {settings.value.skipForwardSec}s</span>
          <select
            value={String(settings.value.skipForwardSec)}
            disabled={busy}
            onChange={(e) =>
              void update({ skipForwardSec: Number((e.target as HTMLSelectElement).value) })
            }
          >
            {[10, 15, 30, 45, 60, 90].map((s) => (
              <option key={s} value={s}>
                {s} seconds
              </option>
            ))}
          </select>
        </label>

        <label class="field">
          <span>Skip back: {settings.value.skipBackSec}s</span>
          <select
            value={String(settings.value.skipBackSec)}
            disabled={busy}
            onChange={(e) =>
              void update({ skipBackSec: Number((e.target as HTMLSelectElement).value) })
            }
          >
            {[5, 10, 15, 30, 45, 60].map((s) => (
              <option key={s} value={s}>
                {s} seconds
              </option>
            ))}
          </select>
        </label>
      </section>

      <section class="section">
        <h2 class="section__title">Appearance</h2>
        <label class="field">
          <span>Theme</span>
          <select
            value={settings.value.theme}
            disabled={busy}
            onChange={(e) =>
              void update({
                theme: (e.target as HTMLSelectElement).value as 'system' | 'light' | 'dark',
              })
            }
          >
            <option value="system">Match system</option>
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </select>
        </label>
      </section>

      <NotificationSettings />
      <DownloadSettings />

      <section class="section">
        <h2 class="section__title">Subscriptions (OPML)</h2>
        <p class="muted">
          Import subscriptions from another podcast app or export your Podcastie subscriptions.
        </p>
        <OpmlManager />
      </section>

      <section class="section">
        <h2 class="section__title">Install</h2>
        <InstallHint />
      </section>

      <section class="section">
        <h2 class="section__title">Privacy</h2>
        <p class="muted">
          Podcastie stores your subscriptions and your place in an episode, and nothing else.
          There is no analytics or advertising. <a href="/privacy">Read the privacy policy</a>.
        </p>
      </section>

      <DeleteAccount />

      <section class="section">
        <h2 class="section__title">About playback on iOS</h2>
        <p class="muted">
          On iPhone and iPad, Safari suspends audio when the app is sent to the background, even
          when installed to the home screen. That is a platform limitation rather than something
          Podcastie can work around — lock-screen controls and background playback are best-effort
          on iOS and reliable on Android and desktop.
        </p>
      </section>
    </div>
  );
}
